import { Fragment, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { fetchDashboardMe } from '@/dashboard/api';
import { DashboardMeProvider } from '@/dashboard/me-context';
import {
  DASHBOARD_NAV_CONFIG,
  resolveNavItems,
  managerNavDestination,
  resolveNavSections,
} from '@/dashboard/nav-config';
import {
  dashboardHomePathForRole,
  dashboardNavSectionsForRole,
  navCanSee,
} from '@/dashboard/role-view';
import {
  dashboardAuthErrorView,
  type DashboardAuthErrorKind,
} from '@/dashboard/auth-error-view';
import RouteErrorBoundary from '@/dashboard/ui/RouteErrorBoundary';
import {
  useVisualActivation,
  VisualVariantContext,
} from '@/dashboard/ui/useVisualActivation';
import type { DashboardMe } from '@/dashboard/types';
import { DoctorPendingProvider, DoctorPendingBadge } from './doctor/DoctorPendingContext';
import './dashboard.css';
import './role-home-parity.css';

const navCls = ({ isActive }: { isActive: boolean }) =>
  isActive ? 'active' : undefined;

// Re-export the pure role-view helpers so existing imports
// (`@/dashboard/DashboardLayout`) continue to work after the helpers
// moved into `./role-view.ts`. The pure module is the canonical
// location and is what the role-view equivalence test imports.
export {
  dashboardHomePathForRole,
  dashboardNavSectionsForRole,
  navCanSee,
};

/**
 * V7-P4-NAV-COHERENCE-AND-ROLE-SHELL-V1: the sidebar renders from THE ONE
 * role-aware nav config (./nav-config.ts) -- the six per-role JSX nav
 * components this file used to define (OwnerNav / AgmNav / CocNav /
 * DoctorNav / ReceptionistNav / CallCenterNav) are retired; their item
 * lists, gates, and law citations live in DASHBOARD_NAV_CONFIG. Rendered
 * output is byte-identical per role (pinned in DashboardLayout.test.tsx).
 *
 * Renders nav sections in the stable config order regardless of which
 * subset is visible (resolveNavSections is FAIL-CLOSED: null me, patient,
 * unknown roles, and unknown section tokens all render nothing). Spacing
 * on second-and-later headers keeps the first visible header flush.
 *
 * NO ROUTE STRING LITERALS BELOW: every nav destination lives in
 * nav-config.ts (guard-pinned in nav-config.test.ts -- the
 * one-source-of-truth guard), so a second scattered nav definition cannot
 * quietly return here.
 */
export function DashboardNavList({
  me,
}: {
  me: DashboardMe | null;
}): JSX.Element {
  const sections = resolveNavSections(me);
  const location = useLocation();
  return (
    <>
      {sections.map((section, index) => {
        const headerCls =
          index > 0
            ? 'dashboard-nav__section dashboard-nav__section--spaced'
            : 'dashboard-nav__section';
        return (
          <Fragment key={section}>
            <strong className={headerCls}>
              {me?.role==='call_center_manager'&&section==='call_center'?'Call center manager':DASHBOARD_NAV_CONFIG[section].header}
            </strong>
            {resolveNavItems(section, me).map((item) =>
              me?.role === 'call_center_manager' && section === 'call_center' ? (
                <Link key={item.label} to={managerNavDestination(item.to,location).to} className={managerNavDestination(item.to,location).active?'active':undefined} aria-current={managerNavDestination(item.to,location).active?'page':undefined}>{item.label}</Link>
              ) : item.plainLink ? (
                <Link key={item.label} to={item.to} className={location.pathname === item.to ? 'active' : undefined} aria-current={location.pathname === item.to ? 'page' : undefined}>
                  {item.label}
                </Link>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.end ? true : undefined}
                  className={navCls}
                >
                  {item.label}{item.count === 'doctorPending' ? <DoctorPendingBadge /> : null}
                </NavLink>
              ),
            )}
          </Fragment>
        );
      })}
    </>
  );
}

interface DashboardLayoutViewProps {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
  /** Distinguishes signed-out from authenticated-but-unresolved so the error
   *  card shows the right message + CTA. Optional: absent falls back to the
   *  historical sign-in card (keeps the pure-render tests unchanged). */
  errorReason?: DashboardAuthErrorKind;
}

/**
 * Pure presentational shell — exported so tests can render it with
 * controlled props via `renderToStaticMarkup` without needing jsdom or
 * a live `/api/dashboard/me`. Mirrors the same loading / error states
 * the data-fetching parent renders.
 */
export function DashboardLayoutView({
  loading,
  error,
  me,
  errorReason,
}: DashboardLayoutViewProps): JSX.Element {
  return (
    <div data-role-home={me?.role === 'doctor' || me?.role === 'receptionist' ? 'true' : undefined} className={`dashboard-shell${me?.role==='call_center_manager'?' cc-manager-shell':''}`}>
      <nav className="dashboard-nav" aria-label="Dashboard sections">
        {loading ? (
          <span
            className="text-xs text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            Loading dashboard…
          </span>
        ) : error ? (
          errorReason === 'unresolved' ? (
            <div className="text-xs text-muted-foreground">
              <p className="font-medium text-foreground">
                Account not set up for the dashboard
              </p>
              <p className="mt-1">
                You are signed in, but this account does not have dashboard
                access yet. Ask your administrator to provision your staff role,
                then reload.
              </p>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">
              <p className="font-medium text-foreground">Sign in to continue</p>
              <p className="mt-1">
                We couldn’t load your dashboard access. Sign in again to retry.
              </p>
              <Link
                to="/login"
                className="mt-2 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
              >
                Sign in
              </Link>
            </div>
          )
        ) : (
          <DashboardNavList me={me} />
        )}
      </nav>
      <div className="dashboard-main">
        {loading ? (
          <p className="loading" role="status" aria-live="polite">
            Loading dashboard…
          </p>
        ) : (
          /* APP-ROUTE-ERROR-BOUNDARY-V1: a routed page that throws during
             render is contained HERE, inside dashboard-main -- the nav and
             the top bar keep rendering, and navigating to another route
             resets the boundary. Pass-through markup when healthy. */
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        )}
      </div>
    </div>
  );
}

export default function DashboardLayout(): JSX.Element {
  // FACTORY-V6-ACTIVATION-V1: the reference visual system (V6) is applied
  // HERE, once, for every route this shell wraps -- the dashboard subtree and
  // the walk-in subtree alike (see App.tsx) -- so the switch holds across
  // navigation, across a refresh, and on the print routes, and comes off again
  // outside the demo allowlist and on unmount. DEFAULT OFF: nothing renders V6
  // until a walker asks for it with the visual=v1 query. This layout still
  // declares NO nav destination of its own -- every route the activation knows
  // about is a cited row in ui/visual-variant.ts, which owns the whole
  // decision; the org-default argument is deliberately not passed (see the
  // hook's own header for why).
  const visualVariant = useVisualActivation();

  const [me, setMe] = useState<DashboardMe | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [errorReason, setErrorReason] = useState<
    DashboardAuthErrorKind | undefined
  >(undefined);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorReason(undefined);
    fetchDashboardMe()
      .then((value) => {
        if (cancelled) return;
        setMe(value);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Reason-aware: distinguishes signed-out from authenticated-but-
        // unresolved (NO_PROFILE) so the sidebar shows the right card + CTA.
        const view = dashboardAuthErrorView(err);
        setError(view.body);
        setErrorReason(view.kind);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Expose the single `/me` payload via context so `RequireNavSection`
  // route guards can reuse it without re-fetching — keeps the sidebar
  // and the route guards reading from the same scope payload.
  return (
    // ONE RESOLVED STATE: the variant the shell just applied to the document
    // is PUBLISHED here, so a screen that renders differently under V6 reads
    // the shell's decision instead of resolving its own from the URL. That
    // split is what let the chrome say v1 while the body underneath rendered
    // the current system on a bare URL.
    <VisualVariantContext.Provider value={visualVariant}>
      <DashboardMeProvider value={{ me, loading, error }}>
        <DoctorPendingProvider me={me}>
        <DashboardLayoutView
          loading={loading}
          error={error}
          me={me}
          errorReason={errorReason}
        />
        </DoctorPendingProvider>
      </DashboardMeProvider>
    </VisualVariantContext.Provider>
  );
}
